import chunk from 'lodash/chunk'
import isEqual from 'lodash/isEqual'
import { LRUCache } from 'lru-cache'
import { expandCompetitors } from './competitors'
import { getRanksForMatches, getRanksForRotatingDoubles } from './rank'
import type {
  AustralianDoublesMatchEntry,
  DoublesMatchEntry,
  PlannerEvent,
  SinglesMatchEntry,
  Stage,
} from './types'
import { GameFormat } from './types'

type UserId = string
type EventId = string
type SeedingNumber = number

// Position indices in the stage opponent list (1-based), not seeding numbers.
// Singles: [side1_pos, side2_pos]
// Doubles: [[team1_p1_pos, team1_p2_pos], [team2_p1_pos, team2_p2_pos]]
type SinglesPlanEntry = [number, number]
type DoublesPlanEntry = [[number, number], [number, number]]
type AustralianDoublesPlanEntry = [number, [number, number]]
type SinglesMatchPlan = SinglesPlanEntry[][][]
type DoublesMatchPlan = DoublesPlanEntry[][][]
type AustralianDoublesMatchPlan = AustralianDoublesPlanEntry[][][]

interface EventSetting<
  TPlan = SinglesMatchPlan | DoublesMatchPlan | AustralianDoublesMatchPlan,
> {
  numberPlayers: number
  // Number of individuals per competitor: 1 for every format where each ranked entity
  // is a single person (Singles, RotatingDoubles, AustralianDoubles), 2 for
  // fixed-partner Doubles (each competitor is a permanent 2-person team).
  competitorSize: number
  groupSize: number
  matchesPerRound: number
  roundsPerStage: number
  numberStages: number
  plan: TPlan
}

const __planner_cache__ = new LRUCache<EventId, EventPlanner>({ max: 10 })

export abstract class EventPlanner {
  static readonly SUPPORTED_PLAYER_COUNTS = [2, 3, 4, 5, 6, 8] as const

  static supportsPlayerCount(n: number, gameFormat?: GameFormat): boolean {
    if (gameFormat === GameFormat.RotatingDoubles) {
      return [3, 4, 5].includes(n)
    }
    if (gameFormat === GameFormat.Doubles) {
      return (
        n % 2 === 0 &&
        (EventPlanner.SUPPORTED_PLAYER_COUNTS as readonly number[]).includes(n / 2)
      )
    }
    return (EventPlanner.SUPPORTED_PLAYER_COUNTS as readonly number[]).includes(n)
  }

  setting: EventSetting
  plan: Stage[]
  _event: PlannerEvent
  protected readonly seedings: UserId[]

  constructor(event: PlannerEvent, setting: EventSetting) {
    this._event = event
    this.plan = []
    this.setting = setting
    this.seedings = expandCompetitors(event.competitors).map((e) => {
      if (!e.player.id) {
        throw new Error('Player user ID is missing')
      }
      return e.player.id
    })
    this.createPlan()
  }

  /**
   * The factory function always returns the same planner instance for the same event ID
   * if the number of players of the event is not changed. However, if the number of
   * players does change, a new planner instance will be created.
   */
  static getPlanner(event: PlannerEvent): EventPlanner {
    const cachedPlanner = __planner_cache__.get(event.id)
    let planner: EventPlanner | undefined
    const numberPlayers = expandCompetitors(event.competitors).length

    if (cachedPlanner) {
      if (cachedPlanner.setting.numberPlayers === numberPlayers) {
        planner = cachedPlanner
        if (!isEqual(event, cachedPlanner._event)) {
          cachedPlanner._event = event
          cachedPlanner.createPlan()
        }
      }
    }

    if (!planner) {
      if (event.gameFormat === GameFormat.RotatingDoubles) {
        if (numberPlayers === 3) {
          planner = new AustralianDoublesPlannerSize3(event)
        } else if (numberPlayers === 4) {
          planner = new RotatingDoublesPlannerSize4(event)
        } else if (numberPlayers === 5) {
          planner = new RotatingDoublesPlannerSize5(event)
        } else {
          throw new Error('Rotating doubles planner only supports 3, 4, or 5 players')
        }
      } else if (event.gameFormat === GameFormat.Doubles) {
        switch (numberPlayers / 2) {
          case 2:
            planner = new DoublesPlannerSize2(event)
            break
          case 3:
            planner = new DoublesPlannerSize3(event)
            break
          case 4:
            planner = new DoublesPlannerSize4(event)
            break
          case 5:
            planner = new DoublesPlannerSize5(event)
            break
          case 6:
            planner = new DoublesPlannerSize6(event)
            break
          case 8:
            planner = new DoublesPlannerSize8(event)
            break
          default:
            throw new Error(
              'Doubles planner only supports 4, 6, 8, 10, 12, or 16 players',
            )
        }
      } else {
        switch (numberPlayers) {
          case 2:
            planner = new MatchPlannerSize2(event)
            break
          case 3:
            planner = new MatchPlannerSize3(event)
            break
          case 4:
            planner = new MatchPlannerSize4(event)
            break
          case 5:
            planner = new MatchPlannerSize5(event)
            break
          case 6:
            planner = new MatchPlannerSize6(event)
            break
          case 8:
            planner = new MatchPlannerSize8(event)
            break
          default:
            throw new Error('No match planner available for the event')
        }
      }
      __planner_cache__.set(event.id, planner)
    }

    return planner
  }

  protected createPlan() {
    const { roundsPerStage, matchesPerRound, numberStages } = this.setting
    const allStagesCompleted =
      this._event.matches.length === matchesPerRound * roundsPerStage * numberStages

    for (
      let stage = 1;
      stage <=
      Math.floor(this._event.matches.length / roundsPerStage / matchesPerRound) +
        (allStagesCompleted ? 0 : 1);
      stage++
    ) {
      this.plan[stage - 1] = this.createMatchPlanForStage(stage)
    }
  }

  abstract createMatchPlanForStage(stage: number): Stage

  get totalMatchCount(): number {
    const { matchesPerRound, roundsPerStage, numberStages } = this.setting
    return matchesPerRound * roundsPerStage * numberStages
  }

  protected getPlayerSeedingNumber = (userId: UserId): SeedingNumber => {
    const idx = this.seedings.indexOf(userId)
    if (idx === -1) {
      throw new Error('Player not found in event')
    }
    return idx + 1
  }

  /**
   * Based on the results of stage matches, return the list of player ranks in each
   * group, indicated by the player's seeding number.
   */
  getStageRankList(stage: number): SeedingNumber[] {
    if (stage < 1 || stage > this.setting.numberStages) {
      throw new Error(`Invalid stage number ${stage}`)
    }
    if (
      this._event.matches.length <
      this.setting.matchesPerRound * this.setting.roundsPerStage * stage
    ) {
      throw new Error('Current stage matches are not completed yet')
    }

    const stageMatches = this._event.matches.filter(
      (m) => Math.ceil(m.round / this.setting.roundsPerStage) === stage,
    )

    const orderedMatches = this.plan[stage - 1].flat().map((entry) => {
      const [s1, s2] = entry as SinglesMatchEntry
      const match = stageMatches.find(
        (m) =>
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          (this.getPlayerSeedingNumber(m.sides[0].players[0].id!) === s1 &&
            // biome-ignore lint/style/noNonNullAssertion: player id is always set
            this.getPlayerSeedingNumber(m.sides[1].players[0].id!) === s2) ||
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          (this.getPlayerSeedingNumber(m.sides[0].players[0].id!) === s2 &&
            // biome-ignore lint/style/noNonNullAssertion: player id is always set
            this.getPlayerSeedingNumber(m.sides[1].players[0].id!) === s1),
      )
      if (!match) {
        console.error(
          `Error finding match between ${s1} and ${s2} from stage ${stage}`,
          stageMatches,
        )
        throw new Error('Match not found for previous stage')
      }
      return match
    })

    const matchesPerGroup = (this.setting.groupSize * (this.setting.groupSize - 1)) / 2
    const opponents = chunk(orderedMatches, matchesPerGroup).flatMap((matchGroup) =>
      getRanksForMatches(matchGroup, this.seedings).map(this.getPlayerSeedingNumber),
    )

    return opponents
  }
}

class MatchPlannerSize6 extends EventPlanner {
  // 6-player two-stage tournament.
  //
  // Stage 1 (group round-robin): 2 groups of 3, interleaved by seeding so each group
  // has similar ability levels.
  //   Group A: seedings 1,3,5 (odd positions) — full round-robin, 3 matches.
  //   Group B: seedings 2,4,6 (even positions) — full round-robin, 3 matches.
  //   3 rounds, 2 parallel matches per round (1 from each group).
  //     Round 1: [1,3], [2,4]
  //     Round 2: [1,5], [2,6]
  //     Round 3: [3,5], [4,6]
  //   Total stage 1: 6 matches.
  //
  // Stage 2 (cross-group head-to-head): 1 round, 3 simultaneous matches.
  //   Re-seeding: positions 1..6 = [A-rank1, A-rank2, A-rank3, B-rank1, B-rank2, B-rank3].
  //   Pairs: [1,4]=A1vB1 (decide final ranks 1,2), [2,5]=A2vB2 (ranks 3,4), [3,6]=A3vB3 (ranks 5,6).
  //   Total stage 2: 3 matches.
  //
  // Total: 9 matches across 4 rounds.
  // Stage detection: rounds 1-3 → stage 1, round 4 → stage 2 (via Math.ceil(round/3)).
  private static readonly STAGE_CUMULATIVE_MATCHES = [6, 9]

  static setting: EventSetting<SinglesMatchPlan> = {
    numberPlayers: 6,
    competitorSize: 1,
    groupSize: 3,
    matchesPerRound: 2,
    roundsPerStage: 3,
    numberStages: 2,
    plan: [
      [
        [
          [1, 3],
          [2, 4],
        ], // Round 1
        [
          [1, 5],
          [2, 6],
        ], // Round 2
        [
          [3, 5],
          [4, 6],
        ], // Round 3
      ],
      [
        [
          [1, 4],
          [2, 5],
          [3, 6],
        ], // Round 4: A1vB1 (ranks 1,2), A2vB2 (ranks 3,4), A3vB3 (ranks 5,6)
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, MatchPlannerSize6.setting)
  }

  protected createPlan() {
    const totalMatches = this._event.matches.length
    const cumulative = MatchPlannerSize6.STAGE_CUMULATIVE_MATCHES
    const allDone = totalMatches === cumulative[cumulative.length - 1]

    const stagesWithData = cumulative.filter((c) => totalMatches >= c).length
    const stagesToPlan = Math.min(
      stagesWithData + (allDone ? 0 : 1),
      this.setting.numberStages,
    )

    this.plan = []
    for (let stage = 1; stage <= stagesToPlan; stage++) {
      this.plan[stage - 1] = this.createMatchPlanForStage(stage)
    }
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as SinglesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([s1, s2]): SinglesMatchEntry => [opponents[s1 - 1], opponents[s2 - 1]],
      ),
    )
  }

  get totalMatchCount(): number {
    return MatchPlannerSize6.STAGE_CUMULATIVE_MATCHES[
      MatchPlannerSize6.STAGE_CUMULATIVE_MATCHES.length - 1
    ]
  }

  // Overrides the base implementation to handle two separate groups in stage 1 and
  // independent head-to-head matches in stage 2 (not a round-robin among all players).
  getStageRankList(stage: number): SeedingNumber[] {
    if (stage < 1 || stage > this.setting.numberStages) {
      throw new Error(`Invalid stage number ${stage}`)
    }
    const requiredMatches = MatchPlannerSize6.STAGE_CUMULATIVE_MATCHES[stage - 1]
    if (this._event.matches.length < requiredMatches) {
      throw new Error('Current stage matches are not completed yet')
    }

    const stageMatches = this._event.matches.filter(
      (m) => Math.ceil(m.round / this.setting.roundsPerStage) === stage,
    )

    const resolveMatch = (s1: SeedingNumber, s2: SeedingNumber) => {
      const match = stageMatches.find(
        (m) =>
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          (this.getPlayerSeedingNumber(m.sides[0].players[0].id!) === s1 &&
            // biome-ignore lint/style/noNonNullAssertion: player id is always set
            this.getPlayerSeedingNumber(m.sides[1].players[0].id!) === s2) ||
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          (this.getPlayerSeedingNumber(m.sides[0].players[0].id!) === s2 &&
            // biome-ignore lint/style/noNonNullAssertion: player id is always set
            this.getPlayerSeedingNumber(m.sides[1].players[0].id!) === s1),
      )
      if (!match) {
        throw new Error(`Match not found for stage ${stage} entry ${s1} vs ${s2}`)
      }
      return match
    }

    const flatPlan = this.plan[stage - 1].flat() as SinglesMatchEntry[]
    const orderedMatches = flatPlan.map(([s1, s2]) => resolveMatch(s1, s2))

    if (stage === 1) {
      // Plan interleaves rounds as [GroupA_match, GroupB_match], so even-index entries
      // are Group A (seedings 1,3,5) and odd-index entries are Group B (seedings 2,4,6).
      const groupAMatches = orderedMatches.filter((_, i) => i % 2 === 0)
      const groupBMatches = orderedMatches.filter((_, i) => i % 2 === 1)

      const groupARanks = getRanksForMatches(groupAMatches, this.seedings).map(
        this.getPlayerSeedingNumber,
      )
      const groupBRanks = getRanksForMatches(groupBMatches, this.seedings).map(
        this.getPlayerSeedingNumber,
      )
      // Returns [A1, A2, A3, B1, B2, B3] — positions 1..6 map to [1,4]=A1vB1,
      // [2,5]=A2vB2, [3,6]=A3vB3 in the stage 2 plan.
      return [...groupARanks, ...groupBRanks]
    }

    // Stage 2: 3 independent head-to-head matches — rank each separately.
    // chunk by 1 per pairing: [A1vB1] → [rank1, rank2], [A2vB2] → [rank3, rank4], etc.
    return chunk(orderedMatches, 1).flatMap((matchGroup) =>
      getRanksForMatches(matchGroup, this.seedings).map(this.getPlayerSeedingNumber),
    )
  }
}

class MatchPlannerSize8 extends EventPlanner {
  static setting: EventSetting<SinglesMatchPlan> = {
    numberPlayers: 8,
    competitorSize: 1,
    groupSize: 2,
    matchesPerRound: 4,
    roundsPerStage: 1,
    numberStages: 3,
    plan: [
      [
        [
          [1, 8],
          [2, 7],
          [3, 6],
          [4, 5],
        ],
      ],
      [
        [
          [1, 7],
          [3, 5],
          [2, 8],
          [4, 6],
        ],
      ],
      [
        [
          [1, 3],
          [2, 4],
          [5, 7],
          [6, 8],
        ],
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, MatchPlannerSize8.setting)
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as SinglesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([s1, s2]): SinglesMatchEntry => [opponents[s1 - 1], opponents[s2 - 1]],
      ),
    )
  }
}

class MatchPlannerSize5 extends EventPlanner {
  // 5-player single-stage round-robin (circle method, bye = position 6).
  // Each round has 2 matches; 1 player sits out. All 10 pairs covered across 5 rounds.
  static setting: EventSetting<SinglesMatchPlan> = {
    numberPlayers: 5,
    competitorSize: 1,
    groupSize: 5,
    matchesPerRound: 2,
    roundsPerStage: 5,
    numberStages: 1,
    plan: [
      [
        [
          [2, 5],
          [3, 4],
        ], // Round 1: P1 sits out
        [
          [1, 2],
          [4, 5],
        ], // Round 2: P3 sits out
        [
          [1, 3],
          [2, 4],
        ], // Round 3: P5 sits out
        [
          [1, 4],
          [3, 5],
        ], // Round 4: P2 sits out
        [
          [1, 5],
          [2, 3],
        ], // Round 5: P4 sits out
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, MatchPlannerSize5.setting)
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as SinglesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([s1, s2]): SinglesMatchEntry => [opponents[s1 - 1], opponents[s2 - 1]],
      ),
    )
  }
}

class MatchPlannerSize2 extends EventPlanner {
  // 2-player single-stage: one head-to-head match.
  static setting: EventSetting<SinglesMatchPlan> = {
    numberPlayers: 2,
    competitorSize: 1,
    groupSize: 2,
    matchesPerRound: 1,
    roundsPerStage: 1,
    numberStages: 1,
    plan: [
      [
        [[1, 2]], // Round 1
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, MatchPlannerSize2.setting)
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as SinglesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([s1, s2]): SinglesMatchEntry => [opponents[s1 - 1], opponents[s2 - 1]],
      ),
    )
  }
}

class MatchPlannerSize3 extends EventPlanner {
  // 3-player single-stage round-robin. 1 match per round, 3 rounds, 1 player sits out each round.
  static setting: EventSetting<SinglesMatchPlan> = {
    numberPlayers: 3,
    competitorSize: 1,
    groupSize: 3,
    matchesPerRound: 1,
    roundsPerStage: 3,
    numberStages: 1,
    plan: [
      [
        [[1, 2]], // Round 1: 3 sits out
        [[1, 3]], // Round 2: 2 sits out
        [[2, 3]], // Round 3: 1 sits out
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, MatchPlannerSize3.setting)
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as SinglesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([s1, s2]): SinglesMatchEntry => [opponents[s1 - 1], opponents[s2 - 1]],
      ),
    )
  }
}

class MatchPlannerSize4 extends EventPlanner {
  // 4-player single-stage round-robin (circle method). 2 matches per round, 3 rounds.
  static setting: EventSetting<SinglesMatchPlan> = {
    numberPlayers: 4,
    competitorSize: 1,
    groupSize: 4,
    matchesPerRound: 2,
    roundsPerStage: 3,
    numberStages: 1,
    plan: [
      [
        [
          [1, 4],
          [2, 3],
        ], // Round 1
        [
          [1, 3],
          [4, 2],
        ], // Round 2
        [
          [1, 2],
          [3, 4],
        ], // Round 3
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, MatchPlannerSize4.setting)
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as SinglesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([s1, s2]): SinglesMatchEntry => [opponents[s1 - 1], opponents[s2 - 1]],
      ),
    )
  }
}

class RotatingDoublesPlannerSize5 extends EventPlanner {
  // 5-player rotating doubles: perfect round-robin where every player partners with
  // every other player exactly once. 5 rounds, 1 match per round (2v2), 1 player sits out.
  //
  // Teams are balanced by minimizing |sum(team1_seedings) - sum(team2_seedings)|.
  // This schedule achieves the mathematical minimum max-diff of 4.
  //
  // Round 1: P1 sits out, P2+P3 vs P4+P5 (diff=4)
  // Round 2: P2 sits out, P1+P4 vs P3+P5 (diff=3)
  // Round 3: P3 sits out, P1+P5 vs P2+P4 (diff=0)
  // Round 4: P4 sits out, P1+P3 vs P2+P5 (diff=3)
  // Round 5: P5 sits out, P1+P2 vs P3+P4 (diff=4)
  static setting: EventSetting<DoublesMatchPlan> = {
    numberPlayers: 5,
    competitorSize: 1,
    groupSize: 5,
    matchesPerRound: 1,
    roundsPerStage: 5,
    numberStages: 1,
    plan: [
      [
        [
          [
            [2, 3],
            [4, 5],
          ],
        ], // Round 1: P1 sits out, diff=4
        [
          [
            [1, 4],
            [3, 5],
          ],
        ], // Round 2: P2 sits out, diff=3
        [
          [
            [1, 5],
            [2, 4],
          ],
        ], // Round 3: P3 sits out, diff=0
        [
          [
            [1, 3],
            [2, 5],
          ],
        ], // Round 4: P4 sits out, diff=3
        [
          [
            [1, 2],
            [3, 4],
          ],
        ], // Round 5: P5 sits out, diff=4
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, RotatingDoublesPlannerSize5.setting)
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as DoublesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([[a, b], [c, d]]): DoublesMatchEntry => [
          [opponents[a - 1], opponents[b - 1]],
          [opponents[c - 1], opponents[d - 1]],
        ],
      ),
    )
  }

  getStageRankList = (stage: number): SeedingNumber[] => {
    if (stage < 1 || stage > this.setting.numberStages) {
      throw new Error(`Invalid stage number ${stage}`)
    }
    if (
      this._event.matches.length <
      this.setting.matchesPerRound * this.setting.roundsPerStage * stage
    ) {
      throw new Error('Current stage matches are not completed yet')
    }

    const stageMatches = this._event.matches.filter(
      (m) => Math.ceil(m.round / this.setting.roundsPerStage) === stage,
    )

    // For doubles, match lookup uses all 4 player seedings
    const orderedMatches = this.plan[stage - 1].flat().map((entry) => {
      const [[a, b], [c, d]] = entry as DoublesMatchEntry
      const match = stageMatches.find((m) => {
        const side1Seeds = m.sides[0].players
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          .map((p) => this.getPlayerSeedingNumber(p.id!))
          .sort()
        const side2Seeds = m.sides[1].players
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          .map((p) => this.getPlayerSeedingNumber(p.id!))
          .sort()
        const team1 = [a, b].sort()
        const team2 = [c, d].sort()
        return (
          (isEqual(side1Seeds, team1) && isEqual(side2Seeds, team2)) ||
          (isEqual(side1Seeds, team2) && isEqual(side2Seeds, team1))
        )
      })
      if (!match) {
        throw new Error(`Match not found for doubles entry [${a},${b}] vs [${c},${d}]`)
      }
      return match
    })

    const allPlayerIds = this.seedings
    const rankedIds = getRanksForRotatingDoubles(orderedMatches, allPlayerIds)
    return rankedIds.map(this.getPlayerSeedingNumber)
  }
}

class AustralianDoublesPlannerSize3 extends EventPlanner {
  // 3-player Australian doubles: each round, one player plays solo against the other two.
  // 3 rounds, 1 match per round. Each player takes the solo role exactly once.
  //
  // Round 1: P1 solo vs P2+P3
  // Round 2: P2 solo vs P1+P3
  // Round 3: P3 solo vs P1+P2
  //
  // Side 1 = solo player, Side 2 = two-player team.
  static setting: EventSetting<AustralianDoublesMatchPlan> = {
    numberPlayers: 3,
    competitorSize: 1,
    groupSize: 3,
    matchesPerRound: 1,
    roundsPerStage: 3,
    numberStages: 1,
    plan: [
      [
        [[1, [2, 3]]], // Round 1: P1 solo
        [[2, [1, 3]]], // Round 2: P2 solo
        [[3, [1, 2]]], // Round 3: P3 solo
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, AustralianDoublesPlannerSize3.setting)
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as AustralianDoublesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([solo, [a, b]]): AustralianDoublesMatchEntry => [
          opponents[solo - 1],
          [opponents[a - 1], opponents[b - 1]],
        ],
      ),
    )
  }

  getStageRankList = (stage: number): SeedingNumber[] => {
    if (stage < 1 || stage > this.setting.numberStages) {
      throw new Error(`Invalid stage number ${stage}`)
    }
    if (
      this._event.matches.length <
      this.setting.matchesPerRound * this.setting.roundsPerStage * stage
    ) {
      throw new Error('Current stage matches are not completed yet')
    }

    const stageMatches = this._event.matches.filter(
      (m) => Math.ceil(m.round / this.setting.roundsPerStage) === stage,
    )

    // Verify each planned match is present (order-independent lookup)
    this.plan[stage - 1].flat().forEach((entry) => {
      const [solo, [a, b]] = entry as AustralianDoublesMatchEntry
      const found = stageMatches.find((m) => {
        const side1Seeds = m.sides[0].players
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          .map((p) => this.getPlayerSeedingNumber(p.id!))
          .sort()
        const side2Seeds = m.sides[1].players
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          .map((p) => this.getPlayerSeedingNumber(p.id!))
          .sort()
        const soloSide = [solo].sort()
        const teamSide = [a, b].sort()
        return (
          (isEqual(side1Seeds, soloSide) && isEqual(side2Seeds, teamSide)) ||
          (isEqual(side1Seeds, teamSide) && isEqual(side2Seeds, soloSide))
        )
      })
      if (!found) {
        throw new Error(
          `Match not found for Australian doubles entry ${solo} vs [${a},${b}]`,
        )
      }
    })

    const allPlayerIds = this.seedings
    return getRanksForRotatingDoubles(stageMatches, allPlayerIds).map(
      this.getPlayerSeedingNumber,
    )
  }
}

class RotatingDoublesPlannerSize4 extends EventPlanner {
  // 4-player rotating doubles: every player partners with every other player exactly once.
  // 3 rounds, 1 match per round (2v2), no one sits out.
  //
  // Teams balanced by minimizing |sum(team1_seedings) - sum(team2_seedings)|:
  // Round 1: P1+P2 vs P3+P4 (diff=4)
  // Round 2: P1+P3 vs P2+P4 (diff=2)
  // Round 3: P1+P4 vs P2+P3 (diff=0)
  static setting: EventSetting<DoublesMatchPlan> = {
    numberPlayers: 4,
    competitorSize: 1,
    groupSize: 4,
    matchesPerRound: 1,
    roundsPerStage: 3,
    numberStages: 1,
    plan: [
      [
        [
          [
            [1, 2],
            [3, 4],
          ],
        ], // Round 1: diff=4
        [
          [
            [1, 3],
            [2, 4],
          ],
        ], // Round 2: diff=2
        [
          [
            [1, 4],
            [2, 3],
          ],
        ], // Round 3: diff=0
      ],
    ],
  }

  constructor(event: PlannerEvent) {
    super(event, RotatingDoublesPlannerSize4.setting)
  }

  createMatchPlanForStage(stage: number): Stage {
    const opponents =
      stage === 1
        ? Array.from({ length: this.setting.numberPlayers }, (_, i) => i + 1)
        : this.getStageRankList(stage - 1)

    return (this.setting.plan as DoublesMatchPlan)[stage - 1].map((round) =>
      round.map(
        ([[a, b], [c, d]]): DoublesMatchEntry => [
          [opponents[a - 1], opponents[b - 1]],
          [opponents[c - 1], opponents[d - 1]],
        ],
      ),
    )
  }

  getStageRankList = (stage: number): SeedingNumber[] => {
    if (stage < 1 || stage > this.setting.numberStages) {
      throw new Error(`Invalid stage number ${stage}`)
    }
    if (
      this._event.matches.length <
      this.setting.matchesPerRound * this.setting.roundsPerStage * stage
    ) {
      throw new Error('Current stage matches are not completed yet')
    }

    const stageMatches = this._event.matches.filter(
      (m) => Math.ceil(m.round / this.setting.roundsPerStage) === stage,
    )

    const orderedMatches = this.plan[stage - 1].flat().map((entry) => {
      const [[a, b], [c, d]] = entry as DoublesMatchEntry
      const match = stageMatches.find((m) => {
        const side1Seeds = m.sides[0].players
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          .map((p) => this.getPlayerSeedingNumber(p.id!))
          .sort()
        const side2Seeds = m.sides[1].players
          // biome-ignore lint/style/noNonNullAssertion: player id is always set
          .map((p) => this.getPlayerSeedingNumber(p.id!))
          .sort()
        const team1 = [a, b].sort()
        const team2 = [c, d].sort()
        return (
          (isEqual(side1Seeds, team1) && isEqual(side2Seeds, team2)) ||
          (isEqual(side1Seeds, team2) && isEqual(side2Seeds, team1))
        )
      })
      if (!match) {
        throw new Error(`Match not found for doubles entry [${a},${b}] vs [${c},${d}]`)
      }
      return match
    })

    const allPlayerIds = this.seedings
    const rankedIds = getRanksForRotatingDoubles(orderedMatches, allPlayerIds)
    return rankedIds.map(this.getPlayerSeedingNumber)
  }
}

/**
 * Builds a synthetic `PlannerEvent` that collapses each fixed-partner team down to a
 * single representative individual (the team's `player1`), and each real match's
 * 2-players-per-side down to that side's representative player. This lets any
 * existing Singles-family planner class run completely unmodified against a team-level
 * view of a fixed-partner Doubles event — it schedules/ranks "teams" exactly as it
 * would schedule/rank "players", with no knowledge that Doubles is involved.
 */
function toTeamRepresentativeEvent(event: PlannerEvent): PlannerEvent {
  // Maps every individual's user ID to their team's representative ID (player1's ID),
  // so a match side can be resolved to its team regardless of partner order.
  const representativeOf = new Map<string, string>()
  for (const competitor of event.competitors) {
    const p1Id = competitor.player1.id
    if (!p1Id) {
      throw new Error('Player user ID is missing')
    }
    representativeOf.set(p1Id, p1Id)
    if (competitor.player2?.id) {
      representativeOf.set(competitor.player2.id, p1Id)
    }
  }

  return {
    id: event.id,
    gameFormat: event.gameFormat,
    competitors: event.competitors.map((competitor) => ({
      id: competitor.id,
      player1: competitor.player1,
      player2: null,
    })),
    matches: event.matches.map((match) => ({
      id: match.id,
      eventId: match.eventId,
      round: match.round,
      sides: match.sides.map((side) => {
        const firstPlayerId = side.players[0]?.id
        const representativeId = firstPlayerId
          ? representativeOf.get(firstPlayerId)
          : undefined
        if (!representativeId) {
          throw new Error('Unable to resolve team for match side')
        }
        return {
          setsWon: side.setsWon,
          players: [{ id: representativeId, openid: representativeId, ratings: [] }],
        }
      }),
      sets: match.sets,
    })),
  }
}

/**
 * Wraps a Singles-family planner class (`MatchPlannerSizeN`) to produce a fixed-partner
 * Doubles planner with an identical schedule and ranking, operating on `N` teams instead
 * of `N` individuals. Fixed-partner Doubles is structurally identical to Singles at the
 * competitor level — partners never play each other and only the team is ranked — so no
 * new match-plan data is needed; this only translates between "competitor slot" and
 * "individual seeding pair" at the boundary (match-plan output, rank-list output).
 */
function makeFixedPartnerDoublesPlanner(
  SinglesPlannerClass: new (event: PlannerEvent) => EventPlanner,
  innerSetting: EventSetting<SinglesMatchPlan>,
) {
  const wrapperSetting: EventSetting<DoublesMatchPlan> = {
    numberPlayers: innerSetting.numberPlayers * 2,
    competitorSize: 2,
    groupSize: innerSetting.groupSize,
    matchesPerRound: innerSetting.matchesPerRound,
    roundsPerStage: innerSetting.roundsPerStage,
    numberStages: innerSetting.numberStages,
    // Unused: createMatchPlanForStage below fully delegates to the wrapped Singles
    // planner instead of reading a static plan array.
    plan: [],
  }

  const translateSlotEntry = (entry: SinglesPlanEntry): DoublesPlanEntry => {
    const [s1, s2] = entry
    return [
      [2 * s1 - 1, 2 * s1],
      [2 * s2 - 1, 2 * s2],
    ]
  }

  return class extends EventPlanner {
    constructor(event: PlannerEvent) {
      super(event, wrapperSetting)
    }

    private buildInnerPlanner(): EventPlanner {
      return new SinglesPlannerClass(toTeamRepresentativeEvent(this._event))
    }

    // Some wrapped classes (e.g. MatchPlannerSize6) override createPlan/
    // totalMatchCount/getStageRankList with irregular per-stage match counts that
    // the base class's generic matchesPerRound*roundsPerStage*numberStages formula
    // can't express. Delegating to a real instance of the wrapped class — rather
    // than recomputing via that generic formula — means the wrapper stays correct
    // for every wrapped class, regular or irregular, without duplicating its logic.
    protected createPlan() {
      const inner = this.buildInnerPlanner()
      this.plan = inner.plan.map((stage) =>
        stage.map((round) =>
          round.map((entry) => translateSlotEntry(entry as SinglesPlanEntry)),
        ),
      )
    }

    get totalMatchCount(): number {
      return this.buildInnerPlanner().totalMatchCount
    }

    createMatchPlanForStage(stage: number): Stage {
      const innerStage = this.buildInnerPlanner().createMatchPlanForStage(stage)
      return innerStage.map((round) =>
        round.map((entry) => translateSlotEntry(entry as SinglesPlanEntry)),
      )
    }

    getStageRankList(stage: number): SeedingNumber[] {
      const slotRanks = this.buildInnerPlanner().getStageRankList(stage)
      return slotRanks.flatMap((s) => [2 * s - 1, 2 * s])
    }
  }
}

// 4-player fixed-partner doubles: two permanent teams, assigned by seeding position
// (positions 1-2 = Team A, positions 3-4 = Team B — the organizer decides this by the
// order players are passed to startEvent). Reuses MatchPlannerSize2's 2-competitor
// schedule (a single head-to-head match) at the team level.
class DoublesPlannerSize2 extends makeFixedPartnerDoublesPlanner(
  MatchPlannerSize2,
  MatchPlannerSize2.setting,
) {}

// 6-player fixed-partner doubles (3 teams): reuses MatchPlannerSize3's 3-competitor
// round-robin schedule at the team level.
class DoublesPlannerSize3 extends makeFixedPartnerDoublesPlanner(
  MatchPlannerSize3,
  MatchPlannerSize3.setting,
) {}

// 8-player fixed-partner doubles (4 teams): reuses MatchPlannerSize4's 4-competitor
// round-robin schedule at the team level.
class DoublesPlannerSize4 extends makeFixedPartnerDoublesPlanner(
  MatchPlannerSize4,
  MatchPlannerSize4.setting,
) {}

// 10-player fixed-partner doubles (5 teams): reuses MatchPlannerSize5's 5-competitor
// round-robin schedule at the team level.
class DoublesPlannerSize5 extends makeFixedPartnerDoublesPlanner(
  MatchPlannerSize5,
  MatchPlannerSize5.setting,
) {}

// 12-player fixed-partner doubles (6 teams): reuses MatchPlannerSize6's 6-competitor
// two-stage schedule at the team level.
class DoublesPlannerSize6 extends makeFixedPartnerDoublesPlanner(
  MatchPlannerSize6,
  MatchPlannerSize6.setting,
) {}

// 16-player fixed-partner doubles (8 teams): reuses MatchPlannerSize8's 8-competitor
// schedule at the team level.
class DoublesPlannerSize8 extends makeFixedPartnerDoublesPlanner(
  MatchPlannerSize8,
  MatchPlannerSize8.setting,
) {}
